/**
 * One factory for every query key in the storefront.
 *
 * The point is exact invalidation. After adding to the cart we invalidate
 * `queryKeys.cart.all` and nothing else; after a wishlist toggle we invalidate
 * the wishlist and the one product. Scattering key arrays through components
 * leads to blanket refetches, which is how a store ends up making forty requests
 * to reflect one change.
 */

export const queryKeys = {
  settings: {
    all: ['settings'],
    public: () => [...queryKeys.settings.all, 'public'],
  },

  products: {
    all: ['products'],
    lists: () => [...queryKeys.products.all, 'list'],
    list: (filters) => [...queryKeys.products.lists(), filters ?? {}],
    facets: (filters) => [...queryKeys.products.all, 'facets', filters ?? {}],
    details: () => [...queryKeys.products.all, 'detail'],
    detail: (slug) => [...queryKeys.products.details(), slug],
    reviews: (productId, filters) => [...queryKeys.products.all, 'reviews', productId, filters ?? {}],
    suggest: (term) => [...queryKeys.products.all, 'suggest', term],
  },

  categories: {
    all: ['categories'],
    list: (filters) => [...queryKeys.categories.all, 'list', filters ?? {}],
    detail: (slug) => [...queryKeys.categories.all, 'detail', slug],
  },

  brands: {
    all: ['brands'],
    list: (filters) => [...queryKeys.brands.all, 'list', filters ?? {}],
    detail: (slug) => [...queryKeys.brands.all, 'detail', slug],
  },

  cart: {
    all: ['cart'],
    detail: (pincode) => [...queryKeys.cart.all, pincode ?? null],
  },

  account: {
    all: ['account'],
    dashboard: () => [...queryKeys.account.all, 'dashboard'],
    profile: () => [...queryKeys.account.all, 'profile'],
    addresses: () => [...queryKeys.account.all, 'addresses'],
    wishlist: () => [...queryKeys.account.all, 'wishlist'],
    orders: (filters) => [...queryKeys.account.all, 'orders', filters ?? {}],
    order: (id) => [...queryKeys.account.all, 'order', id],
  },

  orders: {
    track: (number, email) => ['orders', 'track', number, email],
  },

  blog: {
    all: ['blog'],
    list: (filters) => [...queryKeys.blog.all, 'list', filters ?? {}],
    detail: (slug) => [...queryKeys.blog.all, 'detail', slug],
    categories: () => [...queryKeys.blog.all, 'categories'],
  },

  pages: {
    all: ['pages'],
    detail: (key) => [...queryKeys.pages.all, key],
    footer: () => [...queryKeys.pages.all, 'footer'],
  },

  coupons: {
    public: () => ['coupons', 'public'],
  },
};

export default queryKeys;
